import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  listOverrides,
  getStudentLimit,
  updateStudentLimit,
  deleteStudentLimit,
} from '../../../api/passLimitsApi';
import { listUsers } from '../../../api/adminUsersApi';
import { showSuccess, showError } from '../../../utils/notifications';
import { getContextualError, logError } from '../../../utils/errorHandling';
import CollapsibleSection from '../../common/CollapsibleSection';
import SearchFilter from '../../common/SearchFilter';
import { useLoadingStates } from '../../../hooks/useLoadingStates';
import { useUserNameResolver, userLabel } from '../../../hooks/useUserNameResolver';
import { usePassPolicy } from '../../../hooks/usePassPolicy';
import adminStyles from '../AdminPanel.module.css';
import type { components } from '../../../api/types.gen';

type StudentPassLimit = components['schemas']['StudentPassLimitResponse'];
type UserResponse = components['schemas']['UserResponse'];

/** Which list's row triggered the shared inline edit form. */
type EditingContext = 'search' | 'override';

interface StudentPassLimitsPanelProps {
  schoolId?: number | string;
}

/** Reports whether an in-flight fetch has been superseded/cancelled. */
type IsCancelled = () => boolean;

/** Reads the numeric `status` off an unknown thrown error, if present. */
const errorStatus = (error: unknown): number | undefined => {
  const status = (error as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
};

// New panel (NOT a like-for-like port) built as part of the Firebase ->
// hallpass-backend migration (Admin step 6). Replaces the
// `manageStudentPassLimits` callable's "Student Pass Management" section of
// AdminPanel.js (loadPassManagementData/updateStudentPassLimit/
// updateStudentUnlimitedStatus/resetAllPassLimits handlers, ~lines
// 1380-1516; JSX ~lines 5277-5533) with GET /schools/:schoolId/pass-limits
// (passLimitsApi.js's listOverrides) plus per-student PUT/DELETE
// (updateStudentLimit/deleteStudentLimit).
//
// DATA MODEL CHANGE: the legacy model was a customLimit entry per
// (student, period) — a full grid of 9 columns (Period 1-8 + Advisory) per
// student. The new StudentPassLimit model is ONE number-or-unlimited pair
// per student, full stop (no per-period concept at all — passes.ts's
// maxPerInterval check counts ALL of a student's passes in the current
// policy interval window, not per-period). This panel is therefore built
// ONLY against the new single-limit-per-student model: a numeric limit
// input + an "unlimited" toggle per student. There is no per-period grid
// here, and no attempt is made to auto-convert legacy per-period values
// into anything.
//
// DROPPED: "Reset All to Default" (legacy resetAllPassLimits, ~lines
// 1480-1516) has no backend bulk-reset equivalent — only a per-student
// DELETE exists. Per this migration's established pattern of dropping
// small unreplaced features rather than reimplementing them as N
// sequential calls (see DestinationsPanel.js's subcategories drop and
// ScheduleVariantModal.js's pass-blocking fields drop), the button is
// DROPPED here rather than looped client-side. This is a PROPOSED
// disposition, not a unilateral final decision — see this migration
// step's summary output, which flags it as needing explicit sign-off the
// same way ScheduleVariantModal.js's UNMAPPED FIELDS did.
//
// CROSS-PANEL DEPENDENCY: updateStudentLimit 422s ("School must have a
// PassPolicy interval configured...") when PassPolicyPanel.js's policy has
// no `interval` set. This panel fetches that same policy on mount via
// getPolicy and disables the numeric limit input (with an inline
// explanation) whenever policy.interval is null — "unlimited" can still be
// toggled independent of the policy, since only a non-null maxPerInterval
// triggers that 422 server-side.

const SEARCH_RESULT_LIMIT = 20;

const StudentPassLimitsPanel = ({
  schoolId: schoolIdProp,
}: StudentPassLimitsPanelProps = {}): React.ReactElement => {
  const { currentUser, userRole } = useAuth();
  const schoolId = schoolIdProp ?? currentUser?.schoolId;
  const isSuperAdmin = userRole === 'developer';

  const [loadingStates, setLoading] = useLoadingStates({
    loadOverrides: false,
    searchStudents: false,
    startEdit: false,
    saveLimit: false,
    clearLimit: false,
  });

  // This school's PassPolicy — only its `interval` matters here, to gate
  // the numeric limit input. null means "no policy row yet" (getPolicy
  // 404s in that case), same as "no interval configured".
  const {
    policy,
    loading: loadingPolicy,
    error: policyError,
  } = usePassPolicy(schoolId);

  useEffect(() => {
    if (policyError) {
      logError(policyError, 'loadPolicyForStudentPassLimits');
      showError(
        getContextualError(
          policyError,
          undefined,
          "Failed to load this school's pass policy."
        )
      );
    }
  }, [policyError]);

  const policyIntervalMissing = !policy?.interval;

  // Existing overrides — listOverrides only returns {studentId,
  // maxPerInterval, unlimited}, not a name/email, so names are resolved
  // separately and cached in studentNames (keyed by studentId) as they're
  // looked up, either here or via search below.
  const [overrides, setOverrides] = useState<StudentPassLimit[]>([]);
  const [studentNames, resolveStudentNames, mergeStudentNames] =
    useUserNameResolver('resolveStudentNamesForPassLimits');

  // Guards against a stale response clobbering `overrides` for the wrong
  // schoolId: if schoolId changes while a fetch is in flight, the effect
  // below flips `cancelled` on cleanup so an earlier request that resolves
  // after a later one is dropped instead of overwriting state. Mirrors the
  // cancelled-flag pattern in useAdminSetupChecklist.js's fetchCounts.
  const fetchOverrides = useCallback(
    async (isCancelled: IsCancelled = () => false): Promise<void> => {
      if (!schoolId) return;
      if (!isCancelled()) setLoading('loadOverrides', true);
      try {
        const result = await listOverrides(schoolId);
        if (!isCancelled()) setOverrides(result.data);
        await resolveStudentNames(result.data.map((o) => o.studentId));
      } catch (error) {
        logError(error, 'loadPassLimitOverrides');
        if (!isCancelled()) {
          showError(
            getContextualError(
              error,
              undefined,
              'Failed to load student pass limit overrides. Please try again.'
            )
          );
          setOverrides([]);
        }
      } finally {
        if (!isCancelled()) setLoading('loadOverrides', false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schoolId]
  );

  useEffect(() => {
    let cancelled = false;
    fetchOverrides(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [fetchOverrides]);

  // Student search/picker
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<UserResponse[]>([]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return undefined;
    }
    let active = true;
    setLoading('searchStudents', true);
    listUsers({
      q: searchQuery,
      role: 'STUDENT',
      limit: SEARCH_RESULT_LIMIT,
    })
      .then((result) => {
        if (active) {
          const data =
            isSuperAdmin && schoolId
              ? result.data.filter(
                  (u) => Number(u.schoolId) === Number(schoolId)
                )
              : result.data;
          setSearchResults(data);
          mergeStudentNames(data);
        }
      })
      .catch((error) => {
        logError(error, 'searchStudentsForPassLimits');
        showError(
          getContextualError(
            error,
            undefined,
            'Failed to search students. Please try again.'
          )
        );
        if (active) setSearchResults([]);
      })
      .finally(() => {
        if (active) setLoading('searchStudents', false);
      });
    return () => {
      active = false;
    };
  }, [searchQuery, mergeStudentNames, setLoading, isSuperAdmin, schoolId]);

  // Inline edit form — shared by both the search results and the existing
  // overrides list, keyed by studentId PLUS editingContext ('search' or
  // 'override'). The context disambiguates which list's row is being
  // edited, since the same studentId can appear in both lists at once (a
  // student with an existing override who also matches a live search) —
  // without it, both rows would render the edit form simultaneously.
  const [editingStudentId, setEditingStudentId] = useState<number | null>(null);
  const [editingContext, setEditingContext] = useState<EditingContext | null>(
    null
  );
  const [editMaxPerInterval, setEditMaxPerInterval] = useState<string>('');
  const [editUnlimited, setEditUnlimited] = useState<boolean>(false);

  // Fetches this student's current override directly (rather than reusing
  // the possibly-stale `overrides` list already in state) so the edit form
  // opens with the live server value. A 404 here means "no override to load"
  // and simply opens a blank form, same as the prior overrides.find()
  // returning undefined. We key off the status alone rather than matching
  // the backend's exact 404 message string: the studentId always comes from
  // a just-loaded search result or the overrides list, so the endpoint's
  // other 404 ("student not found", a delete-race) is both vanishingly rare
  // and self-correcting — a subsequent save 404s and surfaces its own error.
  const startEdit = async (
    studentId: number,
    context: EditingContext
  ): Promise<void> => {
    if (!schoolId) return;
    setLoading('startEdit', true);
    try {
      let limit: StudentPassLimit | null = null;
      try {
        limit = await getStudentLimit(schoolId, studentId);
      } catch (error) {
        if (errorStatus(error) !== 404) {
          throw error;
        }
      }
      setEditingStudentId(studentId);
      setEditingContext(context);
      setEditMaxPerInterval(
        limit?.maxPerInterval != null ? String(limit.maxPerInterval) : ''
      );
      setEditUnlimited(limit?.unlimited || false);
    } catch (error) {
      logError(error, 'startEditStudentPassLimit');
      showError(
        getContextualError(
          error,
          undefined,
          'Failed to load pass limit. Please try again.'
        )
      );
    } finally {
      setLoading('startEdit', false);
    }
  };

  const cancelEdit = (): void => {
    setEditingStudentId(null);
    setEditingContext(null);
    setEditMaxPerInterval('');
    setEditUnlimited(false);
  };

  const saveEdit = async (studentId: number): Promise<void> => {
    if (!schoolId) return;
    if (!editUnlimited && editMaxPerInterval !== '' && policyIntervalMissing) {
      showError(
        'Set a Pass Policy interval in Pass Policy above before assigning a numeric per-student limit.'
      );
      return;
    }

    setLoading('saveLimit', true);
    try {
      await updateStudentLimit(schoolId, studentId, {
        maxPerInterval:
          editUnlimited || editMaxPerInterval === ''
            ? null
            : Number(editMaxPerInterval),
        unlimited: editUnlimited,
      });
      showSuccess('Pass limit updated.');
      cancelEdit();
      await fetchOverrides();
    } catch (error) {
      logError(error, 'saveStudentPassLimit');
      if (errorStatus(error) === 422) {
        showError(
          'This school needs a Pass Policy interval configured (see Pass Policy above) before a numeric per-student limit can be set.'
        );
      } else {
        showError(
          getContextualError(
            error,
            undefined,
            'Failed to update pass limit. Please try again.'
          )
        );
      }
    } finally {
      setLoading('saveLimit', false);
    }
  };

  const clearOverride = async (
    studentId: number,
    label: string | null
  ): Promise<void> => {
    if (!window.confirm(`Clear the pass limit override for ${label}?`)) {
      return;
    }
    if (!schoolId) return;

    setLoading('clearLimit', true);
    try {
      await deleteStudentLimit(schoolId, studentId);
      showSuccess('Pass limit override cleared.');
      // Reachable when this student appears in both lists at once: clearing
      // the override from one list's row while the other list's row for the
      // same studentId is mid-edit should close that now-stale edit form.
      if (editingStudentId === studentId) cancelEdit();
      await fetchOverrides();
    } catch (error) {
      logError(error, 'clearStudentPassLimit');
      showError(
        getContextualError(
          error,
          undefined,
          'Failed to clear pass limit. Please try again.'
        )
      );
    } finally {
      setLoading('clearLimit', false);
    }
  };

  const renderEditForm = (studentId: number): React.ReactElement => (
    <div className="edit-destination-form">
      <div className="form-group">
        <label htmlFor={`student-pass-limit-unlimited-${studentId}`}>
          <input
            id={`student-pass-limit-unlimited-${studentId}`}
            type="checkbox"
            checked={editUnlimited}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEditUnlimited(e.target.checked)
            }
            disabled={loadingStates.saveLimit}
          />{' '}
          Unlimited passes
        </label>
      </div>

      <div className="form-group">
        <label htmlFor={`student-pass-limit-max-${studentId}`}>
          Max passes per interval:
        </label>
        <input
          id={`student-pass-limit-max-${studentId}`}
          type="number"
          min="1"
          placeholder={
            policyIntervalMissing ? 'No policy interval set' : 'Unlimited'
          }
          value={editMaxPerInterval}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setEditMaxPerInterval(e.target.value)
          }
          disabled={
            loadingStates.saveLimit || editUnlimited || policyIntervalMissing
          }
        />
        {policyIntervalMissing && !editUnlimited && (
          <small>
            Numeric limits require a Pass Policy interval — configure one in
            Pass Policy above, or grant unlimited passes instead.
          </small>
        )}
      </div>

      <div className={adminStyles['button-group']}>
        <button
          onClick={() => saveEdit(studentId)}
          disabled={loadingStates.saveLimit}
          className={adminStyles['add-btn']}
        >
          {loadingStates.saveLimit ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={cancelEdit}
          disabled={loadingStates.saveLimit}
          className={adminStyles['remove-btn']}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <CollapsibleSection
      title="Student Pass Limits"
      icon="🎯"
      id="student-pass-limits"
      defaultExpanded={true}
    >
      <p>
        Set a per-student pass limit override, or grant unlimited passes. Limits
        apply per interval and school-wide, using the interval from Pass Policy
        above.
      </p>

      {policyIntervalMissing && !loadingPolicy && (
        <p className="no-results">
          No Pass Policy interval is configured for this school yet. Numeric
          per-student limits are disabled until one is set in Pass Policy above;
          unlimited passes can still be granted.
        </p>
      )}

      <div className="student-pass-limits-search">
        <h3>Find a Student</h3>
        <SearchFilter
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search students by name or email..."
          disabled={loadingStates.searchStudents}
        />

        {loadingStates.searchStudents ? (
          <div className="loading">Searching...</div>
        ) : searchQuery.trim() && searchResults.length === 0 ? (
          <p>No students found matching search criteria.</p>
        ) : searchResults.length > 0 ? (
          <div className={adminStyles['teachers-grid']}>
            {searchResults.map((student) => {
              const existing = overrides.find(
                (o) => o.studentId === student.id
              );
              return (
                <div key={student.id} className={adminStyles['teacher-card']}>
                  {editingStudentId === student.id &&
                  editingContext === 'search' ? (
                    renderEditForm(student.id)
                  ) : (
                    <>
                      <div className={adminStyles['teacher-info']}>
                        <strong>{student.name}</strong>
                        <div className="text-muted-sm">
                          {student.email}
                        </div>
                        {existing && (
                          <div className="text-muted-sm">
                            {existing.unlimited
                              ? 'Unlimited passes'
                              : `Limit: ${existing.maxPerInterval ?? 'default'}`}
                          </div>
                        )}
                      </div>
                      <div className={adminStyles['button-group']}>
                        <button
                          onClick={() => startEdit(student.id, 'search')}
                          disabled={loadingStates.startEdit}
                          className={adminStyles['btn-edit']}
                        >
                          {loadingStates.startEdit
                            ? 'Loading...'
                            : existing
                            ? 'Edit Limit'
                            : 'Set Limit'}
                        </button>
                        {existing && (
                          <button
                            onClick={() =>
                              clearOverride(student.id, student.name)
                            }
                            disabled={loadingStates.clearLimit}
                            className={adminStyles['remove-btn']}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      <div className="student-pass-limits-overrides">
        <h3>Current Overrides ({overrides.length})</h3>
        {loadingStates.loadOverrides ? (
          <div className="loading">Loading pass limit overrides...</div>
        ) : overrides.length === 0 ? (
          <p>No student pass limit overrides set yet. Set one above.</p>
        ) : (
          <div className={adminStyles['teachers-grid']}>
            {overrides.map((override) => {
              const student = studentNames[override.studentId];
              const label = userLabel(studentNames, override.studentId);
              return (
                <div key={override.id} className={adminStyles['teacher-card']}>
                  {editingStudentId === override.studentId &&
                  editingContext === 'override' ? (
                    renderEditForm(override.studentId)
                  ) : (
                    <>
                      <div className={adminStyles['teacher-info']}>
                        <strong>{label}</strong>
                        {student?.email && (
                          <div className="text-muted-sm">
                            {student.email}
                          </div>
                        )}
                        <div className="text-muted-sm">
                          {override.unlimited
                            ? 'Unlimited passes'
                            : `Limit: ${override.maxPerInterval ?? 'default'}`}
                        </div>
                      </div>
                      <div className={adminStyles['button-group']}>
                        <button
                          onClick={() =>
                            startEdit(override.studentId, 'override')
                          }
                          disabled={loadingStates.startEdit}
                          className={adminStyles['btn-edit']}
                        >
                          {loadingStates.startEdit ? 'Loading...' : 'Edit'}
                        </button>
                        <button
                          onClick={() =>
                            clearOverride(override.studentId, label)
                          }
                          disabled={loadingStates.clearLimit}
                          className={adminStyles['remove-btn']}
                        >
                          {loadingStates.clearLimit ? 'Clearing...' : 'Clear'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
};

export default StudentPassLimitsPanel;
